/* Progressive enhancement only. Product, search and cart forms use Shopify endpoints. */
(() => {
  'use strict';
  document.documentElement.classList.replace('no-js', 'js');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const returnFocus = new WeakMap();
  const videoObservers = new Map();
  const productInit = new WeakSet();
  const canDialog = typeof HTMLDialogElement !== 'undefined' && 'showModal' in HTMLDialogElement.prototype;
  function openDialog(dialog, trigger) {
    if (!dialog) return;
    if (!canDialog) { dialog.setAttribute('open', ''); return; }
    if (dialog.open) return;
    returnFocus.set(dialog, trigger || document.activeElement);
    dialog.showModal(); document.body.classList.add('modal-open');
    const target = dialog.querySelector('[data-dialog-focus]');
    if (target) requestAnimationFrame(() => target.focus());
  }
  function closeDialog(dialog) { if (!dialog) return; if (canDialog) dialog.close(); else dialog.removeAttribute('open'); }
  function initDialogs(root = document) {
    root.querySelectorAll('dialog:not([data-dialog-ready])').forEach(dialog => {
      dialog.dataset.dialogReady = 'true';
      dialog.addEventListener('close', () => {
        if (!document.querySelector('dialog[open]')) document.body.classList.remove('modal-open');
        const target = returnFocus.get(dialog); if (target?.isConnected) target.focus({preventScroll:true});
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const r = dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) closeDialog(dialog);
      });
    });
  }
  function chooseMedia(product, id, smooth = true) {
    const slides = Array.from(product.querySelectorAll('.gallery-slide'));
    const slide = slides.find(s => s.dataset.mediaId === String(id));
    const stage = product.querySelector('.gallery-stage');
    if (!slide || !stage) return;
    const offset = slide.getBoundingClientRect().left - stage.getBoundingClientRect().left + stage.scrollLeft;
    stage.scrollTo({left:offset,behavior:smooth && !reduced.matches ? 'smooth' : 'auto'});
    product.querySelectorAll('[data-gallery-target]').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.galleryTarget === String(id))));
    slides.filter(s => s !== slide).forEach(s => s.querySelectorAll('video').forEach(v => v.pause()));
  }
  function syncVariant(product, shouldNavigate = false) {
    const select = product.querySelector('[data-variant-select]');
    const selected = select?.selectedOptions[0];
    if (!selected) return;
    // Selling-plan allocation data differs per variant. Re-render on Shopify rather than
    // reusing another variant's allocations or showing an inaccurate subscription price.
    if (shouldNavigate && product.querySelector('[data-plan-select]')) {
      const url = new URL(window.location.href);url.searchParams.set('variant',selected.value);window.location.assign(url);return;
    }
    const price = product.querySelector('[data-price]');
    if (price) { price.textContent = selected.dataset.price || ''; price.dataset.basePrice = price.textContent; }
    const compare = product.querySelector('[data-compare]');
    if (compare) { compare.textContent = selected.dataset.compare || ''; compare.hidden = !selected.dataset.compare; }
    const add = product.querySelector('[data-add]');
    if (add) { add.disabled = selected.dataset.available !== 'true'; add.textContent = add.disabled ? 'Sold out' : 'Add to cart'; }
    const qty = product.querySelector('input[name="quantity"]');
    if (qty) {
      qty.min = selected.dataset.min || '1'; qty.step = selected.dataset.step || '1';
      if (selected.dataset.max) qty.max = selected.dataset.max; else qty.removeAttribute('max');
      qty.value = qty.min;
    }
    if (selected.dataset.image) chooseMedia(product, selected.dataset.image, false);
    if (shouldNavigate && window.history.replaceState && /^https?:$/.test(location.protocol)) {
      const url = new URL(window.location.href);url.searchParams.set('variant',selected.value);history.replaceState({},'',url);
    }
  }
  function initProducts(root = document) {
    root.querySelectorAll('[data-product]').forEach(product => {
      if (productInit.has(product)) return;productInit.add(product);
      syncVariant(product);
      const price = product.querySelector('[data-price]');if (price) price.dataset.basePrice = price.textContent;
      const stage = product.querySelector('.gallery-stage');
      if (stage && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(e => { if (!e.isIntersecting || e.intersectionRatio < .6) return;
            product.querySelectorAll('[data-gallery-target]').forEach(b => b.setAttribute('aria-pressed',String(b.dataset.galleryTarget===e.target.dataset.mediaId)));
          });
        },{root:stage,threshold:.6});
        stage.querySelectorAll('.gallery-slide').forEach(s => observer.observe(s));
      }
      const plan = product.querySelector('[data-plan-select]');if(plan) syncPlan(product);
    });
  }
  function syncPlan(product) {
    const option = product.querySelector('[data-plan-select]')?.selectedOptions[0];
    const price = product.querySelector('[data-price]');
    if (price && option) price.textContent = option.dataset.price || price.dataset.basePrice || price.textContent;
    const compare = product.querySelector('[data-compare]');
    if (compare) { const variant = product.querySelector('[data-variant-select]')?.selectedOptions[0]; compare.hidden = Boolean(option?.value) || !(variant?.dataset.compare || compare.textContent.trim()); }
  }
  function initVideos(root = document) {
    root.querySelectorAll('[data-video-block]').forEach(block => {
      if (videoObservers.has(block)) return;
      const video = block.querySelector('video');const toggle=block.querySelector('[data-video-toggle]');
      if (!video || !toggle || block.dataset.autoPlay !== 'true' || !('IntersectionObserver' in window)) return;
      video.controls=false;toggle.hidden=false;
      const sync = () => {
        toggle.setAttribute('aria-label',video.paused?'Play video':'Pause video');
        toggle.querySelector('[data-play-icon]').hidden=!video.paused;
        toggle.querySelector('[data-pause-icon]').hidden=video.paused;
      };
      const play = () => {const result=video.play();if(result?.catch) result.catch(() => {sync();});};
      toggle.addEventListener('click', () => {if(video.paused){block.dataset.userPaused='false';play();}else{block.dataset.userPaused='true';video.pause();}});
      video.addEventListener('play',sync);video.addEventListener('pause',sync);
      video.addEventListener('error',()=>{video.controls=true;toggle.hidden=true;});
      const observer=new IntersectionObserver(entries=>entries.forEach(entry=>{
        block.dataset.visible=String(entry.isIntersecting);
        const saveData = navigator.connection?.saveData;
        if(entry.isIntersecting && !document.hidden && !reduced.matches && !saveData && block.dataset.userPaused!=='true') play(); else video.pause();
      }),{threshold:.3});
      observer.observe(block);videoObservers.set(block,observer);sync();
    });
  }
  document.addEventListener('click',event=>{
    const target=event.target instanceof Element ? event.target : null;if(!target)return;
    const opener=target.closest('[data-open-dialog]');
    if(opener){openDialog(document.getElementById(opener.dataset.openDialog),opener);return;}
    const closer=target.closest('[data-close-dialog]');if(closer){closeDialog(closer.closest('dialog'));return;}
    const sample=target.closest('[data-sample-open]');
    if(sample){const dialog=document.getElementById('SampleDialog');if(!dialog)return;
      dialog.querySelector('#SampleHeading').textContent=sample.dataset.title||'T-shirt';
      dialog.querySelector('[data-sample-tee]').hidden=sample.dataset.kind==='hoodie';
      dialog.querySelector('[data-sample-hoodie]').hidden=sample.dataset.kind!=='hoodie';
      const image=dialog.querySelector('.sample-dialog-image');image.classList.remove('tone-chalk','tone-ink','tone-sage');image.classList.add(sample.classList.contains('tone-ink')?'tone-ink':sample.classList.contains('tone-sage')?'tone-sage':'tone-chalk');
      openDialog(dialog,sample);return;}
    const stepper=target.closest('[data-quantity]');
    if(stepper){const input=stepper.parentElement.querySelector('input');if(!input)return;try{stepper.dataset.quantity==='up'?input.stepUp():input.stepDown();input.dispatchEvent(new Event('change',{bubbles:true}));}catch(error){console.warn('Invalid quantity configuration',error);}return;}
    const thumb=target.closest('[data-gallery-target]');if(thumb){const product=thumb.closest('[data-product]');if(product)chooseMedia(product,thumb.dataset.galleryTarget);return;}
    const menuLink=target.closest('.menu-links a');if(menuLink)closeDialog(menuLink.closest('dialog'));
  });
  document.addEventListener('change',event=>{
    const target=event.target;if(!(target instanceof Element))return;
    if(target.matches('[data-submit-on-change]'))target.form?.requestSubmit();
    if(target.matches('[data-variant-select]'))syncVariant(target.closest('[data-product]'),true);
    if(target.matches('[data-plan-select]'))syncPlan(target.closest('[data-product]'));
  });
  function init(root=document){initDialogs(root);initProducts(root);initVideos(root);}
  init();document.addEventListener('shopify:section:load',event=>init(event.target));
  document.addEventListener('shopify:section:unload',event=>{videoObservers.forEach((observer,block)=>{if(event.target.contains(block)){observer.disconnect();videoObservers.delete(block);}});});
  document.addEventListener('visibilitychange',()=>{if(document.hidden)document.querySelectorAll('[data-video-block] video').forEach(video=>video.pause());});
  reduced.addEventListener?.('change',()=>{if(reduced.matches)document.querySelectorAll('[data-video-block] video').forEach(video=>video.pause());});
})();
